import type { BriefingSections } from '../hooks/useTeamTodaySurface';

/*
 * ─── The brief's popout REACHABILITY MODEL (phase-4 GUARD) ───
 *
 * `sections.popoutBodies` is a flat map of pregenerated popout bodies, keyed three ways: by
 * conversationId for note-anchored cards, `panel:<source>:<topicId>` for panel topics, and
 * `task:<taskId>` for a task with an id but no note. Nothing in the payload says which keys the UI
 * can actually OPEN — that lives in BriefSurface's render, spread across a dozen call sites — so a
 * stored body could go dark without anything failing anywhere.
 *
 * It did. On brief 230f547b (2026-08-08) 9 of 35 composed bodies had no opener at all: every one was
 * a General-tail row past the sheet's 4-row slice. The renderer slot existed (spec §12 gave tail
 * rows the ruled dialog); the widening simply never revisited a cap cut for an earlier ruling, when
 * a tail row was a receipt rather than a door. Nine paid-for LLM calls, no reader could reach them.
 *
 * WHY THIS MODULE EXISTS RATHER THAN A PARAGRAPH. The repo's own meta-lesson from ~243 bug logs is
 * that documented-but-ungated rules keep recurring and the one rule converted to a gate stopped its
 * bug class. So the reachability rules live HERE, in one exported function, used by the render (for
 * the predicates it shares) and asserted by tests/unit/brief-popout-coverage.test.ts. A new opener
 * that forgets to register here trips the guard; a new CAP cannot strand anything, because caps are
 * preview heights now and this model deliberately ignores them.
 *
 * TWO HONESTY RULES for anyone editing this:
 *  1. Model what the render ACTUALLY does, not what it should do. A class the UI cannot open must be
 *     absent from this set even when that makes a guard red — a red guard is the finding. Three such
 *     classes are absent on purpose today; see NOT REACHABLE below.
 *  2. Evaluate it against the payload as the ROUTE delivers it, not the raw `sections` jsonb.
 *     app/api/surfaces/team/today's filterActionedItems drops needsYourInput / relevantToYourWork /
 *     tasks / around.relevant rows whose briefingItemId the reader has actioned, and filters
 *     conflict tasks by resolution state, so a body can go dark AFTER a dismissal. The route-shaped
 *     payload is the only input that reflects that.
 *
 * NOT REACHABLE, deliberately (a body stored under one of these is waste, and the guard says so):
 *  - a CONFLICT task's conversationId — conflict cards open ConflictResolveModal, which composes on
 *    open; the pass carves them out (spec §10, LOCK).
 *  - the CODA's conversationIds whenever `codaSuppressed` holds — which is the normal case for a
 *    team reader with any material (see that function). This is a live gap, not a settled carve-out:
 *    it is a suppression RULE rather than a render cap, so the tail fix does nothing for it, and it
 *    needs either a spend guard on the MVP side or a product ruling on the suppression itself.
 *  - anything at all on a quiet day, or on a legacy (`version < 2`) brief — see quietDayShell.
 *
 * NOT MODELLED (runtime state, not payload): the reader's session dismissals, live conflict
 * outcomes, and `readOnly`. All three only ever change WHICH opener fires, never add one the payload
 * cannot name, so the model is an upper bound on reachability — the safe direction for a superset
 * assertion. The one case where `readOnly` matters is worth naming, because it looks like a
 * carve-out and is not: a `task:meeting_prep:<eventId>` body is reachable on the ARCHIVED render
 * (the frozen card falls through to the stored body) and NOT on the live one, where GAP 2a routes
 * that card to MeetingPrepModal's live grounded read instead. So the key is counted reachable here
 * — but composing it buys prose that only an archived reader ever sees, which is a spend question
 * for the MVP pass rather than a coverage hole. Pinned by its own test either way.
 *
 * THE MODEL'S ONE BLIND SPOT, stated so nobody mistakes a green guard for proof. This is a
 * DESCRIPTION of the render, so it cannot notice the render walking away from it — re-introduce a
 * `slice()` at a call site and this function keeps cheerfully reporting the row reachable. The
 * mount-level tests are the other half of the pair and they do catch that: brief-general-tail's
 * "GAP 1 — every tail row is reachable" opens the sheet, expands it and clicks row 13, and
 * brief-popout's GAP 2a/2b/4 blocks click each new opener through to a rendered body. Treat the two
 * as one gate; the opener census at the foot of the guard's test file is what links them.
 */

/** The General tail sheet's collapsed-preview height. Exported so the guard can prove reachability
 *  is INDEPENDENT of it: RdCardDialog's rowsCap reveals the remainder in place, so this number
 *  decides how much renders before one click and nothing more. It is not part of the model below. */
export const TAIL_POPOUT_ROW_CAP = 4;

/**
 * The coda lists the reader's OWN notes by construction, so on a team it shows a reader their own
 * work back. Suppress it in the PERSONAL render for a non-solo reader — at RENDER time only:
 * generation still computes the coda, so `hasBaseMaterial` keeps counting it and no
 * reader-only-active day collapses to status:'empty'.
 *
 * RIDER: render it anyway when it is the ONLY material the reader has that day — a receipt beats a
 * blank page that claims to be ready.
 *
 * Solo signal = the payload's own coverage counts (D10's bucket law: `connected <= 1`), the SAME
 * signal the coverage line uses. `coverage` is non-null only for a solo recipient (generate.ts), so
 * an absent coverage block reads as non-solo.
 *
 * HAND-SYNCED with the Slack DM's copy — MVP lib/briefing/format-slack-message.ts `codaSuppressed`.
 * Change both or neither. (B4 / D9.)
 */
export function codaSuppressed(sections: BriefingSections): boolean {
  const solo = (sections.coverage?.connected ?? 2) <= 1;
  if (solo) return false;
  const onlyMaterial =
    (sections.tasks ?? []).length === 0 &&
    (sections.meetings ?? []).length === 0 &&
    (sections.around?.changes ?? []).length === 0 &&
    (sections.around?.relevant ?? []).length === 0;
  return !onlyMaterial;
}

/** True when BriefSurface renders QuietDayShell instead of the brief — every card class, and so
 *  every opener, is absent. Panels are NOT part of the emptiness test, so a quiet day can still
 *  carry panel topics: a stored body for one of them would be unreachable, which is why this
 *  predicate is part of the reachability model rather than an aside. */
export function quietDayShell(sections: BriefingSections): boolean {
  return (
    sections.quietDay === true &&
    (sections.tasks ?? []).length === 0 &&
    (sections.meetings ?? []).length === 0 &&
    (sections.around?.changes ?? []).length === 0 &&
    (sections.around?.relevant ?? []).length === 0 &&
    (sections.knowledgeCaptured ?? []).length === 0
  );
}

/**
 * Every `popoutBodies` key the v2 brief render can open, derived from the openers in BriefSurface.
 *
 * Each branch below names the opener it models. Keep them in the render's own order so a diff of
 * the two reads as a checklist.
 */
export function reachablePopoutKeys(sections: BriefingSections): Set<string> {
  const keys = new Set<string>();
  // The legacy renderer (TeamTodaySurface, version < 2) reads no stored bodies at all, and the
  // quiet-day shell renders no cards.
  // `?? 1` matches BriefSurface's own default exactly (an absent version is a legacy row).
  if ((sections.version ?? 1) < 2 || quietDayShell(sections)) return keys;

  const add = (key: string | null | undefined) => {
    if (key) keys.add(key);
  };

  const around = sections.around ?? {};

  // openChangeCard / openRelevantCard — the General band's inline cards.
  for (const c of around.changes ?? []) add(c.conversationId);
  for (const r of around.relevant ?? []) add(r.conversationId);

  // openTailRow — EVERY tail row. TAIL_POPOUT_ROW_CAP is a preview height, not a cap on this set:
  // RdCardDialog's rowsCap reveals the remainder in place. This is the line the 2026-08-08 brief
  // needed and did not have.
  for (const row of around.tail ?? []) add(row.conversationId);

  // openTaskCard — the priority column. Conflicts are excluded: they open ConflictResolveModal,
  // which composes on open. meeting_prep IS included (see the readOnly note in the header): the live
  // card routes to the prep document, the archived one falls through to the stored body.
  for (const t of sections.tasks ?? []) {
    if (t.stream === 'conflict') continue;
    add(t.conversationId);
    if (t.taskId) add(`task:${t.taskId}`);
  }

  // openTopicObject — one body per panel TOPIC. Panel ITEM rows survive as picker rows with one
  // destination each and carry no body of their own (spec §5 rulings 7-8).
  for (const panel of sections.sourcePanels ?? []) {
    for (const topic of panel.topics ?? []) add(`panel:${panel.source}:${topic.id}`);
  }

  // openDecisionObject — "Decisions forming" cards.
  for (const d of sections.sharedDecisions ?? []) add(d.conversationId);

  // openStandupEntry — "People in motion" cards open their FIRST conversation's body (GAP 4).
  for (const s of sections.standup ?? []) add(s.conversationIds?.[0]);

  // openCodaItem — gated on the render's own suppression rule, not merely on the list's presence.
  if (!codaSuppressed(sections)) {
    for (const k of sections.knowledgeCaptured ?? []) add(k.conversationId);
  }

  return keys;
}

/**
 * The stored bodies no click can reach — the guard's assertion is that this is empty.
 *
 * Returned as a sorted array rather than a boolean so a failure names the dead keys, which is what
 * turns a red test into a diagnosis.
 */
export function unreachablePopoutKeys(sections: BriefingSections): string[] {
  const reachable = reachablePopoutKeys(sections);
  return Object.keys(sections.popoutBodies ?? {})
    .filter(key => !reachable.has(key))
    .sort();
}
